import { getStep1FormConfig } from '@screens/ReportCreation/forms/step1.config';
import { getStep2DetailFormConfig } from '@screens/ReportCreation/forms/step2.config';
import { getStep3DetailFormConfig } from '@screens/ReportCreation/forms/step3.config';
import type {
  FormField,
  FormLayout,
  FormOption,
} from '@screens/ReportCreation/reportCreation.types';
import type { SupportedLanguage } from '@store/i18n';
import type { TFunction } from 'i18next';
import { formatDiagnosticColumnHeader } from '../csvTemplate.utils';
import type { ValidationRule } from '../../types/csvValidation.types';
import {
  DIAGNOSTIC_PROCEDURES_TEMPLATE_LIMIT,
  getDiagnosticBasePath,
} from '@screens/ReportCreation/components/CsvImport/constants/csvTemplate.constants';
import type { ReferentialsResponseDto } from '@api/WhisApi/generated';

const buildSingleFieldRule = (
  field: FormField,
  header: string,
  t: TFunction,
  language: SupportedLanguage,
  diagnosticIndex?: number,
): ValidationRule => {
  const baseRule: ValidationRule = {
    header: header,
    fieldId: String(field.id),
    fieldType: 'text',
    required: field.required,
    diagnosticIndex,
  };

  switch (field.type) {
    case 'toggle-group': {
      const options = field.options.map((option: FormOption) =>
        option.label.toLowerCase(),
      );
      const optionValueByLabel = field.options.reduce<Record<string, string>>(
        (acc, option) => {
          acc[option.label.toLowerCase()] = option.value;
          return acc;
        },
        {},
      );
      return {
        ...baseRule,
        fieldType: 'option',
        options,
        optionValueByLabel,
      };
    }

    case 'select':
    case 'autocomplete-search': {
      const optionsArray =
        field.options.length > 0
          ? field.options.map((option: FormOption) =>
              option.label.toLowerCase(),
            )
          : undefined;
      const optionValueByLabel =
        field.options.length > 0
          ? field.options.reduce<Record<string, string>>((acc, option) => {
              acc[option.label.toLowerCase()] = option.value;
              return acc;
            }, {})
          : undefined;
      return {
        ...baseRule,
        fieldType: optionsArray ? 'option' : 'dynamic',
        options: optionsArray,
        dynamicOptions: field.dynamicOptions,
        optionValueByLabel,
      };
    }

    case 'species-search':
      return {
        ...baseRule,
        fieldType: 'dynamic',
      };

    case 'checkbox':
      return {
        ...baseRule,
        fieldType: 'option',
        options: [
          t('reportCreation.step2.options.yesNo.yes', {
            lng: language,
          }).toLowerCase(),
          t('reportCreation.step2.options.yesNo.no', {
            lng: language,
          }).toLowerCase(),
        ],
        optionValueByLabel: {
          [t('reportCreation.step2.options.yesNo.yes', {
            lng: language,
          }).toLowerCase()]: 'yes',
          [t('reportCreation.step2.options.yesNo.no', {
            lng: language,
          }).toLowerCase()]: 'no',
        },
      };

    case 'date':
      return {
        ...baseRule,
        fieldType: 'date',
      };

    case 'numeric':
      return {
        ...baseRule,
        fieldType: 'numeric',
      };

    case 'textarea':
      return {
        ...baseRule,
        maxLength: field.maxLength,
      };

    default:
      return baseRule;
  }
};

const appendSectionRules = ({
  layout,
  t,
  language,
  headerFormatter,
  diagnosticIndex,
}: {
  layout: FormLayout;
  t: TFunction;
  language: SupportedLanguage;
  headerFormatter: (label: string) => string;
  diagnosticIndex?: number;
}): ValidationRule[] => {
  return (
    layout.sections?.flatMap((section) =>
      section.fields.flatMap((field): ValidationRule[] => {
        if (field.type === 'location') {
          return [
            {
              header: headerFormatter(
                t('pinPosition.latitude', { lng: language }),
              ),
              fieldId: 'location.latitude',
              fieldType: 'latitude',
              required: field.required,
              diagnosticIndex,
            },
            {
              header: headerFormatter(
                t('pinPosition.longitude', { lng: language }),
              ),
              fieldId: 'location.longitude',
              fieldType: 'longitude',
              required: field.required,
              diagnosticIndex,
            },
          ];
        }

        const header = headerFormatter(
          field.label ?? section.title ?? String(field.id),
        );
        return [
          buildSingleFieldRule(field, header, t, language, diagnosticIndex),
        ];
      }),
    ) ?? []
  );
};

const buildDiagnosticRules = (
  layout: FormLayout,
  t: TFunction,
  language: SupportedLanguage,
): ValidationRule[] => {
  const repeatableSection = layout.repeatableSections?.[0];
  if (!repeatableSection) {
    return [];
  }
  const rules: ValidationRule[] = [];

  for (
    let index = 0;
    index < DIAGNOSTIC_PROCEDURES_TEMPLATE_LIMIT;
    index += 1
  ) {
    const diagnosticBasePath = getDiagnosticBasePath(index);
    rules.push(
      ...appendSectionRules({
        layout: { sections: repeatableSection.sections },
        t,
        language,
        headerFormatter: (label) =>
          formatDiagnosticColumnHeader(t, language, index + 1, label),
        diagnosticIndex: index,
      }).map((rule) => ({
        ...rule,
        fieldId: `${diagnosticBasePath}.${rule.fieldId}`,
      })),
    );
  }

  return rules;
};

export const buildValidationRules = (
  t: TFunction,
  language: SupportedLanguage,
  referentials?: ReferentialsResponseDto,
): ValidationRule[] => {
  const step1 = getStep1FormConfig(t, language);
  const step2 = getStep2DetailFormConfig(t, language, referentials);
  const step3 = getStep3DetailFormConfig(t, language);

  return [
    ...appendSectionRules({
      layout: step1,
      t,
      language,
      headerFormatter: (label) => label,
    }),
    ...appendSectionRules({
      layout: step2,
      t,
      language,
      headerFormatter: (label) => label,
    }),
    ...buildDiagnosticRules(step3, t, language),
  ];
};
